import { apiDeleteJSON, apiGetJSON, apiPostBlob, apiPostMultipart, apiPutMultipart } from "../client.js";
import { isAbortError } from "../core/request-coordinator.js";
import { loadWorkspaceContentFile, loadWorkspaceContentTree } from "./loaders.js";
import {
  joinWorkspacePath,
  resolveWorkspaceDeleteTarget,
  resolveWorkspaceFileUploadTarget,
  resolveWorkspaceTreeUploadTarget,
  workspacePathDirname,
  workspaceRequestCurrent
} from "./state.js";

export async function submitFileUpload(state, apiKey, workspaceID, workspaceItem, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  if (state.workspaceID !== workspaceID) throw new Error("Open the canonical content panel first.");
  if (!workspaceItem) throw new Error("Workspace not found.");
  const uploadFile = state.fileUploadFile;
  if (!(uploadFile instanceof File)) throw new Error("Choose a file to upload first.");
  const targetPath = resolveWorkspaceFileUploadTarget(state);
  if (!targetPath) throw new Error("Choose or enter a target path first.");
  const confirmMutation = deps.confirm || ((message) => window.confirm(message));
  const warning = state.selectedFilePath && targetPath === state.selectedFilePath
    ? `Replace canonical file ${targetPath}?`
    : `Upload ${uploadFile.name || "file"} to ${targetPath} on ${workspaceItem.default_branch || "main"}?`;
  if (!confirmMutation(warning)) return;
  const uploadFileRequest = deps.apiPutMultipart || apiPutMultipart;
  const formData = new FormData();
  formData.set("file", uploadFile, uploadFile.name || "upload.bin");
  if (state.commit) formData.set("expected_base_commit", String(state.commit));
  if (state.fileUploadExecutable) formData.set("executable", "true");
  Object.assign(state, { fileUploading: true, fileUploadProgress: 0, mutationError: "" });
  if (options.render) options.render();
  try {
    options.setStatus?.(`Uploading ${uploadFile.name || "file"}...`, "busy");
    const result = await uploadFileRequest(
      apiKey,
      `/v1/workspaces/${encodeURIComponent(workspaceID)}/content/file?path=${encodeURIComponent(targetPath)}`,
      formData,
      {
        onProgress(progress) {
          if (state.workspaceID !== workspaceID) return;
          Object.assign(state, { fileUploading: true, fileUploadProgress: Number(progress.percent || 0) });
          options.render?.();
        },
        signal: mutationSignal(options)
      }
    );
    if (!workspaceRequestCurrent(state, workspaceID, options)) return;
    Object.assign(state, {
      fileUploading: false,
      fileUploadProgress: 100,
      fileUploadFile: null,
      fileUploadName: "",
      fileUploadExecutable: false,
      fileUploadPath: targetPath,
      mutationError: "",
      lastChange: result,
      activeMutation: "",
      feedbackDismissed: false
    });
    await refreshAfterChange(state, apiKey, workspaceID, workspacePathDirname(targetPath), targetPath, options, deps);
    options.setStatus?.("Canonical file committed.", "ok");
  } catch (error) {
    if (isAbortError(error) || !workspaceRequestCurrent(state, workspaceID, options)) {
      return;
    }
    Object.assign(state, {
      fileUploading: false,
      fileUploadProgress: 0,
      mutationError: error.message || "File upload failed."
    });
    options.render?.();
    throw error;
  }
}

export async function submitFileEdit(state, apiKey, workspaceID, workspaceItem, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  if (state.workspaceID !== workspaceID) throw new Error("Open the canonical content panel first.");
  if (!workspaceItem) throw new Error("Workspace not found.");
  const filePath = state.selectedFilePath;
  if (!filePath) throw new Error("No file selected.");
  const saveFileRequest = deps.apiPutMultipart || apiPutMultipart;
  const content = state.fileDraftContent ?? "";
  const blob = new Blob([content], { type: "text/plain" });
  const fileName = filePath.split("/").pop() || "file.txt";
  const formData = new FormData();
  formData.set("file", blob, fileName);
  if (state.commit) formData.set("expected_base_commit", String(state.commit));
  Object.assign(state, { fileSaving: true, fileDraftContent: content, mutationError: "" });
  options.render?.();
  try {
    options.setStatus?.(`Saving ${filePath}...`, "busy");
    const result = await saveFileRequest(
      apiKey,
      `/v1/workspaces/${encodeURIComponent(workspaceID)}/content/file?path=${encodeURIComponent(filePath)}`,
      formData,
      { signal: mutationSignal(options) }
    );
    if (!workspaceRequestCurrent(state, workspaceID, options)) return;
    Object.assign(state, {
      fileSaving: false,
      fileEditing: false,
      fileDraftContent: null,
      mutationError: "",
      lastChange: result,
      activeMutation: "",
      feedbackDismissed: false
    });
    await refreshAfterChange(state, apiKey, workspaceID, workspacePathDirname(filePath), filePath, options, deps);
    options.setStatus?.("File saved.", "ok");
  } catch (error) {
    if (isAbortError(error) || !workspaceRequestCurrent(state, workspaceID, options)) {
      return;
    }
    Object.assign(state, { fileSaving: false, mutationError: error.message || "Save failed." });
    options.render?.();
    throw error;
  }
}

export async function submitTreeUpload(state, apiKey, workspaceID, workspaceItem, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  if (state.workspaceID !== workspaceID) throw new Error("Open the canonical content panel first.");
  const archiveFile = state.treeUploadFile;
  if (!(archiveFile instanceof File)) throw new Error("Choose a zip archive first.");
  const targetPath = resolveWorkspaceTreeUploadTarget(state);
  const confirmMutation = deps.confirm || ((message) => window.confirm(message));
  const warning = `Merge ${archiveFile.name || "archive.zip"} into canonical directory ${targetPath || "/"}? Files absent from the archive will be kept.`;
  if (!confirmMutation(warning)) return;
  const uploadTreeRequest = deps.apiPostMultipart || apiPostMultipart;
  const formData = new FormData();
  formData.set("archive", archiveFile, archiveFile.name || "upload-tree.zip");
  if (state.commit) formData.set("expected_base_commit", String(state.commit));
  Object.assign(state, { treeUploading: true, treeUploadProgress: 0, mutationError: "" });
  options.render?.();
  try {
    options.setStatus?.(`Uploading ${archiveFile.name || "archive"}...`, "busy");
    const suffix = targetPath ? `?path=${encodeURIComponent(targetPath)}` : "";
    const result = await uploadTreeRequest(
      apiKey,
      `/v1/workspaces/${encodeURIComponent(workspaceID)}/content/upload-tree${suffix}`,
      formData,
      {
        onProgress(progress) {
          if (state.workspaceID !== workspaceID) return;
          Object.assign(state, { treeUploading: true, treeUploadProgress: Number(progress.percent || 0) });
          options.render?.();
        },
        signal: mutationSignal(options)
      }
    );
    if (!workspaceRequestCurrent(state, workspaceID, options)) return;
    Object.assign(state, {
      treeUploading: false,
      treeUploadProgress: 100,
      treeUploadFile: null,
      treeUploadName: "",
      treeUploadPath: targetPath,
      mutationError: "",
      lastChange: result,
      activeMutation: "",
      feedbackDismissed: false
    });
    await refreshAfterChange(state, apiKey, workspaceID, targetPath, "", options, deps);
    options.setStatus?.("Canonical tree upload committed.", "ok");
  } catch (error) {
    if (isAbortError(error) || !workspaceRequestCurrent(state, workspaceID, options)) {
      return;
    }
    Object.assign(state, {
      treeUploading: false,
      treeUploadProgress: 0,
      mutationError: error.message || "Tree upload failed."
    });
    options.render?.();
    throw error;
  }
}

export async function submitDeletePath(state, apiKey, workspaceID, workspaceItem, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  if (state.workspaceID !== workspaceID) throw new Error("Open the canonical content panel first.");
  const targetPath = resolveWorkspaceDeleteTarget(state);
  if (!targetPath) throw new Error("Choose a file or directory path to delete.");
  const confirmMutation = deps.confirm || ((message) => window.confirm(message));
  const warning = `Delete canonical path ${targetPath} from ${workspaceItem?.default_branch || "main"}?`;
  if (!confirmMutation(warning)) return;
  const deletePathRequest = deps.apiDeleteJSON || apiDeleteJSON;
  Object.assign(state, { deletingPath: true, mutationError: "" });
  options.render?.();
  try {
    options.setStatus?.(`Deleting ${targetPath}...`, "busy");
    const payload = state.commit ? { expected_base_commit: String(state.commit) } : {};
    const result = await deletePathRequest(
      apiKey,
      `/v1/workspaces/${encodeURIComponent(workspaceID)}/content/path?path=${encodeURIComponent(targetPath)}`,
      payload,
      { signal: mutationSignal(options) }
    );
    if (!workspaceRequestCurrent(state, workspaceID, options)) return;
    const nextTreePath = state.path === targetPath ? workspacePathDirname(targetPath) : String(state.path || workspacePathDirname(targetPath));
    Object.assign(state, {
      deletingPath: false,
      deletePath: "",
      selectedFilePath: "",
      file: null,
      fileError: "",
      mutationError: "",
      lastChange: result,
      activeMutation: "",
      feedbackDismissed: false
    });
    await refreshAfterChange(state, apiKey, workspaceID, nextTreePath, "", options, deps);
    options.setStatus?.("Canonical path deleted.", "ok");
  } catch (error) {
    if (isAbortError(error) || !workspaceRequestCurrent(state, workspaceID, options)) {
      return;
    }
    Object.assign(state, { deletingPath: false, mutationError: error.message || "Delete failed." });
    options.render?.();
    throw error;
  }
}

export async function downloadSnapshot(state, apiKey, workspaceID, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  const exportSnapshot = deps.apiPostBlob || apiPostBlob;
  const createObjectURL = deps.createObjectURL || ((blob) => window.URL.createObjectURL(blob));
  const revokeObjectURL = deps.revokeObjectURL || ((url) => window.URL.revokeObjectURL(url));
  const createLink = deps.createLink || (() => document.createElement("a"));
  const appendLink = deps.appendLink || ((link) => document.body.appendChild(link));
  const removeLink = deps.removeLink || ((link) => link.remove());
  const schedule = deps.schedule || ((callback) => window.setTimeout(callback, 0));
  const ref = state.workspaceID === workspaceID ? String(state.refInput || "").trim() : "";
  if (state.workspaceID === workspaceID) {
    state.exporting = true;
    options.render?.();
  }
  try {
    options.setStatus?.("Preparing snapshot export...", "busy");
    const exported = await exportSnapshot(apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/export`, ref ? { ref } : {}, {
      signal: mutationSignal(options)
    });
    const downloadURL = createObjectURL(exported.blob);
    const link = createLink();
    link.href = downloadURL;
    link.download = exported.filename || `workspace-${workspaceID}.zip`;
    appendLink(link);
    link.click();
    removeLink(link);
    schedule(() => revokeObjectURL(downloadURL));
    options.setStatus?.("Snapshot export downloaded.", "ok");
  } finally {
    if (state.workspaceID === workspaceID) {
      state.exporting = false;
      options.render?.();
    }
  }
}

export async function refreshImportRecord(state, apiKey, workspaceID, importID, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  if (!workspaceID || !importID) return null;
  const fetchJSON = deps.apiGetJSON || apiGetJSON;
  const record = await fetchJSON(apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/imports/${encodeURIComponent(importID)}`);
  if (workspaceRequestCurrent(state, workspaceID, options)) {
    state.lastImport = record;
    if (options.clearError) state.importError = "";
    options.render?.();
  }
  return record;
}

export async function submitSnapshotImport(state, apiKey, workspaceID, workspaceItem, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  if (state.workspaceID !== workspaceID) throw new Error("Open the canonical content panel first.");
  if (!workspaceItem) throw new Error("Workspace not found.");
  const packageFile = state.importFile;
  if (!(packageFile instanceof File)) throw new Error("Choose a snapshot package first.");
  const confirmMutation = deps.confirm || ((message) => window.confirm(message));
  const warning = "Import replaces the canonical workspace snapshot. Files missing from the package will be deleted. Continue?";
  if (!confirmMutation(warning)) return;
  const importSnapshot = deps.apiPostMultipart || apiPostMultipart;
  const formData = new FormData();
  formData.set("package", packageFile, packageFile.name || "snapshot.zip");
  formData.set("mode", "replace_snapshot");
  if (String(state.importMessage || "").trim()) formData.set("message", String(state.importMessage || "").trim());
  if (String(state.importExpectedBaseCommit || "").trim()) formData.set("expected_base_commit", String(state.importExpectedBaseCommit || "").trim());
  Object.assign(state, { importing: true, importProgress: 0, importError: "", lastImport: state.lastImport || null });
  options.render?.();
  try {
    options.setStatus?.(`Importing snapshot into ${workspaceItem.name}...`, "busy");
    const result = await importSnapshot(
      apiKey,
      `/v1/workspaces/${encodeURIComponent(workspaceID)}/import`,
      formData,
      {
        onProgress(progress) {
          if (state.workspaceID !== workspaceID) return;
          Object.assign(state, { importing: true, importProgress: Number(progress.percent || 0) });
          options.render?.();
        },
        signal: mutationSignal(options)
      }
    );
    if (!workspaceRequestCurrent(state, workspaceID, options)) return;
    Object.assign(state, {
      importing: false,
      importProgress: 100,
      importError: "",
      importFile: null,
      importFileName: "",
      importMessage: "",
      lastImport: result,
      importExpectedBaseCommit: String(result.base_commit || "")
    });
    if (options.refreshCollections) await options.refreshCollections();
    if (!workspaceRequestCurrent(state, workspaceID, options)) return;
    const loadTree = deps.loadWorkspaceContentTree || loadWorkspaceContentTree;
    await loadTree(state, apiKey, workspaceID, {
      ref: state.refInput,
      path: "",
      preserveFile: false,
      render: options.render,
      request: options.request
    });
    options.setStatus?.("Snapshot import completed.", "ok");
  } catch (error) {
    if (isAbortError(error) || !workspaceRequestCurrent(state, workspaceID, options)) {
      return;
    }
    let importRecord = null;
    if (error.importID) {
      try {
        importRecord = await refreshImportRecord(state, apiKey, workspaceID, error.importID, { render: options.render, request: options.request }, deps);
      } catch (_error) {
        importRecord = null;
      }
    }
    Object.assign(state, {
      importing: false,
      importProgress: 0,
      importError: error.message || "Snapshot import failed.",
      lastImport: importRecord || state.lastImport
    });
    options.render?.();
    throw error;
  }
}

export async function submitDropUpload(state, apiKey, workspaceID, workspaceItem, files, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  if (state.workspaceID !== workspaceID) throw new Error("Open the canonical content panel first.");
  if (!workspaceItem) throw new Error("Workspace not found.");
  if (!files || files.length === 0) return;
  const uploadFileRequest = deps.apiPutMultipart || apiPutMultipart;
  const total = files.length;
  Object.assign(state, {
    dropUploading: true,
    dropUploadTotal: total,
    dropUploadDone: 0,
    dropUploadError: "",
    mutationError: "",
    activeMutation: "",
    feedbackDismissed: false
  });
  options.render?.();
  let lastResult = null;
  let lastTargetPath = "";
  try {
    for (let i = 0; i < total; i++) {
      const file = files[i];
      const targetPath = joinWorkspacePath(state.path, file.name);
      lastTargetPath = targetPath;
      options.setStatus?.(`Uploading ${i + 1}/${total}: ${file.name}...`, "busy");
      const formData = new FormData();
      formData.set("file", file, file.name || "upload.bin");
      if (state.commit) formData.set("expected_base_commit", String(state.commit));
      lastResult = await uploadFileRequest(
        apiKey,
        `/v1/workspaces/${encodeURIComponent(workspaceID)}/content/file?path=${encodeURIComponent(targetPath)}`,
        formData,
        { signal: mutationSignal(options) }
      );
      if (!workspaceRequestCurrent(state, workspaceID, options)) return;
      if (lastResult?.result_commit) {
        state.commit = lastResult.result_commit;
      }
      state.dropUploadDone = i + 1;
      options.render?.();
    }
    Object.assign(state, {
      dropUploading: false,
      lastChange: lastResult,
      feedbackDismissed: false
    });
    await refreshAfterChange(state, apiKey, workspaceID, state.path, total === 1 ? lastTargetPath : "", options, deps);
    options.setStatus?.(`${total} file${total === 1 ? "" : "s"} uploaded.`, "ok");
  } catch (error) {
    if (isAbortError(error) || !workspaceRequestCurrent(state, workspaceID, options)) return;
    Object.assign(state, {
      dropUploading: false,
      mutationError: error.message || "Drop upload failed."
    });
    options.render?.();
    throw error;
  }
}

async function refreshAfterChange(state, apiKey, workspaceID, treePath, previewPath, options = {}, deps = {}) {
  const loadTree = deps.loadWorkspaceContentTree || loadWorkspaceContentTree;
  const loadFile = deps.loadWorkspaceContentFile || loadWorkspaceContentFile;
  if (options.refreshCollections) await options.refreshCollections();
  if (!workspaceRequestCurrent(state, workspaceID, options)) {
    return;
  }
  await loadTree(state, apiKey, workspaceID, {
    ref: state.refInput,
    path: treePath,
    preserveFile: false,
    render: options.render,
    request: options.request
  });
  if (previewPath) {
    await loadFile(state, apiKey, workspaceID, previewPath, {
      render: options.render,
      request: options.request
    });
  }
}

function mutationSignal(options = {}) {
  return options.signal || options.request?.signal || null;
}
